"use client";

import { useEffect } from "react";

import { usePathname, useRouter, useSearchParams } from "next/navigation";

import { DISCONTINUED_CATEGORY_PARAM, DISCONTINUED_SKU_PARAM } from "@/app/[locale]/(default)/shop/constants";
import { useFilter } from "@/context/FilterContext";

import type { CategoryChildrenType } from "../_components/ProductsContent";
import { fetchDiscontinuedProductName } from "../_utils/fetchDiscontinuedProductName";

type DiscontinuedProductHandlerProps = {
  subcategories?: CategoryChildrenType[] | null;
  locale?: string;
};

/**
 * Client component that handles discontinued product URL params.
 * Must be wrapped in Suspense boundary due to useSearchParams usage.
 */
export function DiscontinuedProductHandler({ subcategories, locale }: DiscontinuedProductHandlerProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const { setInitialCategories, setDiscontinuedProductName } = useFilter();

  const redirectCategory = searchParams?.get(DISCONTINUED_CATEGORY_PARAM);
  const discontinuedProductSku = searchParams?.get(DISCONTINUED_SKU_PARAM);
  const hasDiscontinuedParams = !!(redirectCategory || discontinuedProductSku);

  useEffect(() => {
    if (redirectCategory && subcategories && locale) {
      const redirectCategoryMatch = subcategories.find(
        (subcategory) =>
          subcategory.name?.toLowerCase() === redirectCategory.toLowerCase() || subcategory.url_key === redirectCategory
      );

      if (redirectCategoryMatch?.uid) {
        setInitialCategories([redirectCategoryMatch.uid]);
      }
    }
  }, [redirectCategory, subcategories, locale, setInitialCategories]);

  useEffect(() => {
    if (!hasDiscontinuedParams) return;

    const processAndCleanParams = async () => {
      if (discontinuedProductSku && locale) {
        const productName = await fetchDiscontinuedProductName(discontinuedProductSku, locale);

        if (productName) {
          setDiscontinuedProductName(productName);
        }
      }

      const newParams = new URLSearchParams(searchParams.toString());
      newParams.delete(DISCONTINUED_CATEGORY_PARAM);
      newParams.delete(DISCONTINUED_SKU_PARAM);

      const newUrl = newParams.toString() ? `${pathname}?${newParams.toString()}` : pathname;

      router.replace(newUrl, { scroll: false });
    };

    processAndCleanParams();
  }, [
    hasDiscontinuedParams,
    discontinuedProductSku,
    locale,
    pathname,
    router,
    searchParams,
    setDiscontinuedProductName,
  ]);

  return null;
}
